// src/pages/T178AccessSelector.jsx
// T178 Access Type Selector — Intermediate page before individual flow

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const T178AccessSelector = ({ userAlias }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // Deep link handling: if URL has query params (e.g., from expiry emails), redirect to individual flow
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('originalRequestId') || params.get('type') === 'renewal') {
            navigate(`/request/individual${location.search}`, { replace: true });
        }
    }, [location.search, navigate]);

    const accessOptions = [
        {
            id: 'individual',
            icon: '🔑',
            title: 'Individual Permissions Access',
            description: 'Request access to specific T178 permissions for individual users. Supports all risk levels:',
            riskLevels: [
                { code: 'LRP', name: 'Low Risk Permissions', color: '#166534', bg: '#dcfce7' },
                { code: 'MRP', name: 'Medium Risk Permissions', color: '#92400e', bg: '#fef3c7' },
                { code: 'HRP', name: 'High Risk Permissions', color: '#9333ea', bg: '#f3e8ff' },
                { code: 'CRP', name: 'Critical Risk Permissions', color: '#dc2626', bg: '#fef2f2' }
            ],
            badge: 'Active',
            badgeBg: '#dcfce7',
            badgeColor: '#166534',
            badgeBorder: '#bbf7d0',
            active: true,
            route: '/request/individual'
        },
        {
            id: 'ruleset',
            icon: '📋',
            title: 'Ruleset Access',
            description: 'Request rule-based access for Low Risk and Medium Risk permissions. Ideal for teams needing standardized access patterns.',
            riskLevels: [
                { code: 'LRP', name: 'Low Risk Permissions', color: '#166534', bg: '#dcfce7' },
                { code: 'MRP', name: 'Medium Risk Permissions', color: '#92400e', bg: '#fef3c7' }
            ],
            note: '⚠️ Only Low Risk (LRP) and Medium Risk (MRP) permissions are eligible for Ruleset Access. For High Risk (HRP) and Critical Risk (CRP) permissions, please use Individual Permissions Access.',
            badge: 'Active',
            badgeBg: '#dcfce7',
            badgeColor: '#166534',
            badgeBorder: '#bbf7d0',
            active: true,
            route: '/request/ruleset'
        },
        {
            id: 'bindle',
            icon: '📦',
            title: 'Bindle Access',
            description: 'Request bindle-level access for T178 permissions. Grants access based on bindle membership.',
            riskLevels: null,
            badge: 'Active',
            badgeBg: '#dcfce7',
            badgeColor: '#166534',
            badgeBorder: '#bbf7d0',
            active: true,
            route: '/request/bindle'
        }
    ];

    return (
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 24px' }}>
            {/* Title */}
            <div style={{ marginBottom: '32px' }}>
                <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#111', margin: '0 0 6px' }}>🔐 T178 Permission Request</h1>
                <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Choose how you'd like to request access to Tenant 178 permissions</p>
            </div>

            {/* Access Type Cards — Horizontal */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                {accessOptions.map(option => (
                    <div
                        key={option.id}
                        onClick={() => option.active && option.route && navigate(option.route)}
                        style={{
                            padding: '24px 20px',
                            borderRadius: '12px',
                            border: option.active ? '2px solid #22c55e' : '1px solid #e2e6ea',
                            background: option.active ? '#fff' : '#fafbfc',
                            cursor: option.active ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            opacity: option.active ? 1 : 0.7,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                            boxShadow: option.active ? '0 2px 8px rgba(34,197,94,0.08)' : 'none',
                            position: 'relative'
                        }}
                        onMouseEnter={(e) => { if (option.active) { e.currentTarget.style.boxShadow = '0 6px 20px rgba(34,197,94,0.15)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                        onMouseLeave={(e) => { if (option.active) { e.currentTarget.style.boxShadow = '0 2px 8px rgba(34,197,94,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; } }}
                    >
                        {/* Badge top-right */}
                        <span style={{
                            position: 'absolute', top: '12px', right: '12px',
                            padding: '2px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: '700',
                            backgroundColor: option.badgeBg,
                            color: option.badgeColor,
                            border: `1px solid ${option.badgeBorder}`
                        }}>
                            {option.badge}
                        </span>

                        {/* Icon */}
                        <div style={{ fontSize: '32px' }}>{option.icon}</div>

                        {/* Title */}
                        <h3 style={{ fontSize: '16px', fontWeight: '700', color: option.active ? '#111' : '#666', margin: 0 }}>
                            {option.title}
                        </h3>

                        {/* Description */}
                        <p style={{ fontSize: '13px', color: '#555', margin: 0, lineHeight: '1.5' }}>
                            {option.description}
                        </p>

                        {/* Risk Level Badges */}
                        {option.riskLevels && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {option.riskLevels.map(rl => (
                                    <span key={rl.code} style={{
                                        padding: '3px 10px', borderRadius: '14px', fontSize: '11px', fontWeight: '600',
                                        backgroundColor: rl.bg, color: rl.color, border: `1px solid ${rl.color}22`
                                    }}>
                                        {rl.code} — {rl.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Note */}
                        {option.note && (
                            <div style={{ padding: '8px 10px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '11px', color: '#92400e', lineHeight: '1.4' }}>
                                {option.note}
                            </div>
                        )}

                        {/* CTA for active */}
                        {option.active && (
                            <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: '600', color: '#22c55e' }}>
                                Request Access →
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default T178AccessSelector;
